import { useEffect, useState } from "react";
import { BellRing, BellOff, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { enablePush, disablePush, pushStatus, pushSupport } from "@/lib/push";
import { studio } from "@/lib/studioApi";

export const PushToggle = ({ compact = false }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const support = pushSupport();

  const refresh = () => pushStatus().then(setState).catch(() => setState({ devices: 0, this_device: false }));
  useEffect(() => { refresh(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (state?.this_device) {
        await disablePush();
        toast.success("Notifications désactivées sur cet appareil.");
      } else {
        await enablePush();
        toast.success("Notifications activées : vous serez prévenu même app fermée.");
      }
      await refresh();
    } catch (e) { toast.error(e.message || "Activation impossible."); }
    finally { setBusy(false); }
  };

  const on = Boolean(state?.this_device);
  const previewDigest = async () => {
    try {
      const { data } = await studio.post("/push/digest/preview");
      toast.message(data.title, { description: data.body });
    } catch { toast.error("Aperçu indisponible."); }
  };
  const hint = support === "ios_install"
    ? "iPhone : Partager → « Sur l'écran d'accueil », puis activez depuis l'icône Devisto."
    : support === "denied" ? "Bloquées dans le navigateur : autorisez-les dans ses réglages."
    : on ? `Actif · résumé du soir à 18h${state.devices > 1 ? ` · ${state.devices} appareils` : ""}` : "Devis signé, facture payée, résumé du soir à 18h… même app fermée.";

  return (
    <div data-testid="push-toggle" className={`flex items-center justify-between gap-3 ${compact ? "px-4 py-3" : "rounded-2xl border border-[#E2E7F7] bg-white p-4"}`}>
      <div className="flex min-w-0 items-center gap-2.5">
        <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${on ? "bg-emerald-100 text-emerald-600" : "bg-[#EEF1FC] text-[#3A5CC8]"}`}>
          <Smartphone className="h-4 w-4" strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#0F2645]">Alertes sur ce téléphone</p>
          <p data-testid="push-hint" className="line-clamp-2 text-xs leading-snug text-[#5C6A87]">{hint}</p>
          <button type="button" data-testid="push-digest-preview" onClick={previewDigest} className="mt-1 inline-flex min-h-[32px] items-center text-xs font-semibold text-[#3A5CC8] hover:underline">
            Aperçu du résumé du soir
          </button>
        </div>
      </div>
      <button
        type="button"
        data-testid="push-toggle-btn"
        disabled={busy || support === "unsupported" || support === "denied" || state === null}
        onClick={toggle}
        className={`inline-flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-full px-3 text-xs font-bold transition-colors disabled:opacity-40 ${on ? "bg-[#F3F5FC] text-[#5C6A87] hover:bg-[#E2E7F7]" : "bg-[#0F2645] text-white hover:bg-[#3A5CC8]"}`}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : on ? <BellOff className="h-3.5 w-3.5" /> : <BellRing className="h-3.5 w-3.5" />}
        {on ? "Désactiver" : "Activer"}
      </button>
    </div>
  );
};
